/**
 * Login screen. POST /api/session with ER credentials; sets httpOnly cookie.
 * On success, navigates to the Base screen.
 */

import * as React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { LogIn } from "lucide-react";

import { ApiError, login as apiLogin } from "@/api/client";
import { queryKeys } from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [erLogin, setErLogin] = React.useState("");
  const [erPassword, setErPassword] = React.useState("");

  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const mutation = useMutation({
    mutationFn: () => apiLogin(erLogin.trim(), erPassword),
    onSuccess: (info) => {
      queryClient.setQueryData(queryKeys.session, info);
      navigate(from, { replace: true });
    },
  });

  const errorText = mutation.isError ? loginErrorText(mutation.error) : null;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!erLogin.trim() || !erPassword || mutation.isPending) return;
    mutation.mutate();
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <LogIn className="h-5 w-5" />
          </div>
          <h1 className="text-xl font-semibold tracking-tight">База активистов</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Вход по учётной записи Единой России
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-xl border bg-card p-6 text-card-foreground shadow-sm"
          data-testid="login"
        >
          <div className="space-y-1.5">
            <Label htmlFor="er_login">Логин ЕР</Label>
            <Input
              id="er_login"
              type="text"
              autoComplete="username"
              autoFocus
              placeholder="name@region.er.ru"
              value={erLogin}
              onChange={(e) => setErLogin(e.target.value)}
              disabled={mutation.isPending}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="er_password">Пароль</Label>
            <Input
              id="er_password"
              type="password"
              autoComplete="current-password"
              value={erPassword}
              onChange={(e) => setErPassword(e.target.value)}
              disabled={mutation.isPending}
            />
          </div>

          {errorText && (
            <p className="text-sm text-destructive" role="alert">
              {errorText}
            </p>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={mutation.isPending || !erLogin.trim() || !erPassword}
          >
            {mutation.isPending ? <Spinner /> : null}
            Войти
          </Button>
        </form>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          Сессия хранится на сервере. Учётные данные не сохраняются в браузере.
        </p>
      </div>
    </div>
  );
}

function loginErrorText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) return "Неверный логин или пароль.";
    if (error.status === 403) return "Доступ запрещён. Проверьте учётную запись.";
    if (error.status >= 500) return "Сервер недоступен. Попробуйте позже.";
    return error.message || "Не удалось войти.";
  }
  return "Не удалось войти. Проверьте соединение.";
}
